import React, { PropsWithChildren, Suspense, useRef, useState } from "react"
import { useEventListener, useLockedBody } from "usehooks-ts"
import LoadingSpinner from "@/components/LoadingSpinner"

export type SheetContext = {
  isOpen: boolean
}

function SheetOverlay({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  return (
    <div
      onClick={() => {
        onClose()
      }}
      className={`absolute left-0 top-0 z-20 h-full w-full bg-black transition-opacity duration-300 ${
        isOpen ? "opacity-10" : "pointer-events-none opacity-0"
      }`}
    ></div>
  )
}

function SheetSlide({
  children,
  isOpen,
}: PropsWithChildren & { isOpen: boolean }) {
  return (
    <div
      className={`xh-[100dvh] fixed bottom-0 left-0 z-30 flex h-0 w-full flex-col items-center justify-end overflow-hidden drop-shadow-[rgba(0,0,0,0.25)_0_-10px_30px] transition-[height,shadow,drop-shadow] duration-300 ${
        isOpen ? "h-[94dvh]" : ""
      }`}
    >
      <div
        className={
          "flex h-full w-full flex-col items-center rounded-t-2xl bg-white"
        }
      >
        <div
          className={
            "relative flex h-full w-full max-w-[550px] flex-col-reverse"
          }
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function SheetTopNavigation({ onClose }: { onClose: () => void }) {
  return (
    <div
      className={
        "border-b-1 flex w-full flex-row justify-end rounded-t-2xl border-b bg-gray-50 p-[20px] transition peer-[.sheet-content-scroll-at-top]:border-b-0 peer-[.sheet-content-scroll-at-top]:bg-white"
      }
    >
      <button
        className={"font-medium leading-none text-[#4349FE]"}
        type={"button"}
        onClick={() => {
          onClose()
        }}
      >
        Done
      </button>
    </div>
  )
}

function SheetContentSection({ children }: PropsWithChildren) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [isScrollAtTop, setIsScrollAtTop] = useState(true)

  const onScroll = (event: Event) => {
    if ((event.currentTarget as HTMLDivElement).scrollTop <= 10) {
      setIsScrollAtTop(true)
    } else if (isScrollAtTop) {
      setIsScrollAtTop(false)
    }
  }

  useEventListener("scroll", onScroll, contentRef)

  return (
    <div
      ref={contentRef}
      className={`peer flex flex-1 flex-col gap-[20px] overflow-y-scroll ${
        isScrollAtTop ? "sheet-content-scroll-at-top" : ""
      }`}
    >
      {children}
    </div>
  )
}

export type SheetProps = {
  isOpen: boolean
  onClose: () => void
}

export default function Sheet({
  children,
  isOpen,
  onClose,
}: PropsWithChildren & SheetProps) {
  useLockedBody(isOpen)

  return (
    <>
      <SheetOverlay isOpen={isOpen} onClose={onClose} />
      <SheetSlide isOpen={isOpen}>
        <SheetContentSection>
          <Suspense
            fallback={
              <div className={"flex h-full w-full items-center justify-center"}>
                <LoadingSpinner />
              </div>
            }
          >
            {children}
          </Suspense>
        </SheetContentSection>
        <SheetTopNavigation onClose={onClose} />
      </SheetSlide>
    </>
  )
}
